import {
  createBucketListItemFn,
  deleteBucketListItemFn,
  getBucketItemUploadUrlsFn,
  getUpdateBucketItemUploadUrlsFn,
  updateBucketListItemFn,
} from "~/lib/functions/bucket-list";
import {
  deleteCategoryOfLifeAndFileFn,
  finalizeCategoryFn,
  getCategoryUploadUrlFn,
  updateCategoryFn,
} from "~/lib/functions/category-of-life";
import {
  deleteEngResourceAndFileFn,
  updateEngResourceFn,
  getEngResourceDownloadInfoFn,
  getEngResourceUploadUrlFn,
  getUpdateEngResourceUploadUrlFn,
  finalizeEngResourceFn,
} from "~/lib/functions/engagement";
import {
  BUCKET_LIST_ITEM_MAX_IMAGES,
  type GetBucketItemUploadUrlsFormData,
} from "~/lib/schemas/bucket-list";
import type { EncryptedCategoryData } from "~/lib/schemas/category-of-life";
import type {
  FinalizeUpdateEngResourceData,
  GetResourceUploadUrlData,
} from "~/lib/schemas/engagement";
import type { KeyGrants } from "~/lib/schemas/key-grants";

export async function uploadFileToS3(
  url: string,
  file: File,
  contentType: string,
) {
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
    },
    body: file,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("S3 Upload Error:", errorText);
    throw new Error("Failed to upload file to S3.");
  }
}

/** Creates an encrypted category, uploading its image first when there is one. */
export async function createAndUploadCategoryFile(
  data: EncryptedCategoryData & { categoryId: string },
  file?: File,
) {
  if (!file) {
    return await finalizeCategoryFn({ data: { ...data, imageUrl: null } });
  }

  const response = await getCategoryUploadUrlFn({
    data: {
      categoryId: data.categoryId,
      filename: file.name,
      contentType: file.type,
    },
  });

  if (!response) {
    throw new Error(`Failed to get upload URL`);
  }

  try {
    await uploadFileToS3(response.signedUrl, file, file.type);
    return await finalizeCategoryFn({
      data: { ...data, imageUrl: response.key },
    });
  } catch (error) {
    console.error("Upload failed.", error);
    throw error;
  }
}

export async function updateCategoryFile(
  data: EncryptedCategoryData & {
    id: string;
    imageUrl?: string | null;
    shareCategoryName?: string;
  },
  file?: File,
) {
  if (!file) {
    return await updateCategoryFn({
      data: { ...data, imageUrl: data.imageUrl ?? null },
    });
  }
  const getUrlResponse = await getCategoryUploadUrlFn({
    data: {
      categoryId: data.id,
      filename: file.name,
      contentType: file.type,
    },
  });

  if (!getUrlResponse) {
    throw new Error(`Failed to get update URL`);
  }

  const { signedUrl, key } = getUrlResponse;

  await uploadFileToS3(signedUrl, file, file.type);

  const finalizeResponse = await updateCategoryFn({
    data: { ...data, imageUrl: key },
  });

  if (!finalizeResponse) {
    throw new Error(`Failed to finalize update`);
  }

  return finalizeResponse;
}

export async function deleteCategoryFile(id: string) {
  const response = await deleteCategoryOfLifeAndFileFn({
    data: { id },
  });
  if (!response.ok) {
    throw new Error(`Failed to delete category`);
  }
}

/** Creates an encrypted bucket-list item, uploading its images first. */
export async function createAndUploadBucketListFile(
  data: {
    id: string;
    order?: number;
    encryptedDescription: string;
    iv: string;
    keyGrants: KeyGrants;
  },
  files: File[],
) {
  if (files.length > BUCKET_LIST_ITEM_MAX_IMAGES) {
    console.warn(
      `Attempted to upload ${files.length} images, limiting to ${BUCKET_LIST_ITEM_MAX_IMAGES}`,
    );
  }

  const filesToUpload =
    files.length > BUCKET_LIST_ITEM_MAX_IMAGES
      ? files.slice(0, BUCKET_LIST_ITEM_MAX_IMAGES)
      : files;

  if (filesToUpload.length === 0) {
    return await createBucketListItemFn({ data: { ...data, images: [] } });
  }

  const uploadRequestData: GetBucketItemUploadUrlsFormData = {
    id: data.id,
    files: filesToUpload.map((file, index) => ({
      tempId: `temp-${index}`,
      filename: file.name,
      contentType: file.type,
    })),
  };

  const response = await getBucketItemUploadUrlsFn({ data: uploadRequestData });

  if (!response || response.uploads.length !== filesToUpload.length) {
    throw new Error(`Failed to get all upload URLs for BucketList`);
  }

  const { uploads } = response;
  const uploadPromises: Promise<void>[] = [];
  const imageKeys: string[] = [];

  uploads.forEach((uploadInfo, index) => {
    const file = filesToUpload[index];
    const uploadPromise = uploadFileToS3(uploadInfo.uploadUrl, file, file.type)
      .then(() => {
        imageKeys.push(uploadInfo.s3Key);
      })
      .catch((error) => {
        console.error(`Upload failed for file ${file.name}.`, error);
        throw new Error(`Upload failed for ${file.name}: ${error.message}`);
      });

    uploadPromises.push(uploadPromise);
  });

  try {
    await Promise.all(uploadPromises);
    const bucketList = await createBucketListItemFn({
      data: { ...data, images: imageKeys },
    });
    return bucketList;
  } catch (error) {
    console.error("BucketList file upload or finalization failed.", error);
    throw error;
  }
}

export async function deleteBucketListItem(itemId: string) {
  const response = await deleteBucketListItemFn({
    data: { id: itemId },
  });
  if (!response.ok) {
    throw new Error(`Failed to delete bucket list item`);
  }
  console.log(`BucketList item ${itemId} deleted successfully.`);
}

export async function updateBucketListItemFile(
  data: {
    id: string;
    existingImageKeys: string[];
    encryptedDescription: string;
    iv: string;
    keyGrants: KeyGrants;
  },
  newFiles: File[],
) {
  const itemId = data.id;
  const initialKeys = [...data.existingImageKeys];

  const maxNewFiles = Math.max(
    0,
    BUCKET_LIST_ITEM_MAX_IMAGES - initialKeys.length,
  );

  if (newFiles.length > maxNewFiles) {
    console.warn(
      `Attempted to upload ${newFiles.length} images, limiting to ${maxNewFiles} (Total max: ${BUCKET_LIST_ITEM_MAX_IMAGES})`,
    );
  }

  const filesToUpload =
    newFiles.length > maxNewFiles ? newFiles.slice(0, maxNewFiles) : newFiles;

  if (filesToUpload.length === 0) {
    return await updateBucketListItemFn({
      data: {
        id: itemId,
        imageKeys: initialKeys,
        encryptedDescription: data.encryptedDescription,
        iv: data.iv,
        keyGrants: data.keyGrants,
      },
    });
  }

  const uploadRequestData = {
    id: itemId,
    images: filesToUpload.map((file, index) => ({
      tempId: `update-temp-${index}`,
      filename: file.name,
      contentType: file.type,
    })),
  };

  const response = await getUpdateBucketItemUploadUrlsFn({
    data: uploadRequestData,
  });

  if (!response || response.uploads.length !== filesToUpload.length) {
    throw new Error(
      `Failed to get all update upload URLs for BucketList ID: ${itemId}`,
    );
  }

  const { uploads } = response;
  const uploadPromises: Promise<void>[] = [];
  const newlyUploadedKeys: string[] = [];

  uploads.forEach((uploadInfo, index) => {
    const file = filesToUpload[index];
    const uploadPromise = uploadFileToS3(uploadInfo.uploadUrl, file, file.type)
      .then(() => {
        newlyUploadedKeys.push(uploadInfo.s3Key);
      })
      .catch((error) => {
        console.error(`Upload failed for new file ${file.name}.`, error);
        throw new Error(`Upload failed for ${file.name}: ${error.message}`);
      });

    uploadPromises.push(uploadPromise);
  });

  try {
    await Promise.all(uploadPromises);

    const finalImageKeys = [...initialKeys, ...newlyUploadedKeys];

    const bucketList = await updateBucketListItemFn({
      data: {
        id: itemId,
        imageKeys: finalImageKeys,
        encryptedDescription: data.encryptedDescription,
        iv: data.iv,
        keyGrants: data.keyGrants,
      },
    });
    return bucketList;
  } catch (error) {
    console.error("BucketList file update or finalization failed.", error);
    throw error;
  }
}

export async function createAndUploadFile(
  data: GetResourceUploadUrlData,
  file: File,
) {
  const response = await getEngResourceUploadUrlFn({
    data: {
      ...data,
      filename: file.name,
      contentType: file.type,
    },
  });

  if (!response) {
    throw new Error(`Failed to get upload URL`);
  }

  const { url } = response.signed;
  const resourceId = response.resourceId;

  console.log(`Resource ID generated: ${resourceId}. Uploading to S3...`);

  try {
    await uploadFileToS3(url, file, file.type);

    const resource = await finalizeEngResourceFn({
      data: {
        resourceId: resourceId,
        filename: file.name,
        engagementId: data.engagementId,
        sessionId: data.sessionId,
        title: data.title,
        uploadModel: data.uploadModel,
        tagIds: data.tagIds,
        expirationDate: data.expirationDate,
      },
    });
    return resource;
  } catch (error) {
    console.error("Upload failed.", error);
    throw error;
  }
}

export async function updateFileAndResource(
  data: FinalizeUpdateEngResourceData,
  file: File,
) {
  const getUrlResponse = await getUpdateEngResourceUploadUrlFn({
    data: {
      resourceId: data.resourceId,
      filename: file.name,
      contentType: file.type,
    },
  });

  if (!getUrlResponse) {
    throw new Error(`Failed to get update URL`);
  }

  const { url } = getUrlResponse;

  await uploadFileToS3(url, file, file.type);

  const finalizeResponse = await updateEngResourceFn({
    data: {
      ...data,
    },
  });

  if (!finalizeResponse) {
    throw new Error(`Failed to finalize update`);
  }

  return finalizeResponse;
}

export async function getFileDownloadLink(resourceId: string) {
  const response = await getEngResourceDownloadInfoFn({ data: { resourceId } });

  if (!response) {
    throw new Error(`Failed to get download info`);
  }

  return {
    filename: response.filename,
    fileUrl: response.fileUrl,
  };
}

export async function downloadFileLink(resourceId: string) {
  return await getFileDownloadLink(resourceId);
}

export async function deleteResource(resourceId: string) {
  const response = await deleteEngResourceAndFileFn({ data: { resourceId } });

  if (!response.ok) {
    throw new Error(`Failed to delete resource`);
  }

  console.log(`Resource ${resourceId} deleted successfully.`);
}
